import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { Plane as FerieIcon, Send, CheckCircle2, XCircle, CircleSlash, Clock3, Edit as EditIcon, Delete as DeleteIcon, CircleHelp, RefreshCw, Save, Plus, FileText } from 'lucide-react';
import { feriePermessiMock } from '../mocks/hr/personaleMockData';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useJob } from '../context/JobContext';

const statoColor = {
  bozza: 'default',
  inviata: 'warning',
  approvata: 'success',
  rifiutata: 'error',
  annullata: 'default'
};

const formatDateToItalian = (value) => {
  if (!value) return '-';
  const raw = String(value);
  const datePart = raw.includes('T') ? raw.split('T')[0] : raw;
  const isoMatch = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day}/${month}/${year}`;
  }
  return datePart;
};

const formatTimeToHHmm = (value) => {
  if (!value) return '';
  return String(value).slice(0, 5);
};

const computeHoursFromTimes = (oraInizio, oraFine) => {
  if (!oraInizio || !oraFine) return '';
  const [sh, sm] = String(oraInizio).split(':').map(Number);
  const [eh, em] = String(oraFine).split(':').map(Number);
  if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return '';
  const start = (sh * 60) + sm;
  const end = (eh * 60) + em;
  if (end <= start) return '';
  const hours = (end - start) / 60;
  return hours.toFixed(2);
};

const MONTH_OPTIONS = [
  { value: 1, label: 'Gennaio' },
  { value: 2, label: 'Febbraio' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Aprile' },
  { value: 5, label: 'Maggio' },
  { value: 6, label: 'Giugno' },
  { value: 7, label: 'Luglio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Settembre' },
  { value: 10, label: 'Ottobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Dicembre' }
];

const StatWidgetHr = ({ title, value, icon: Icon, color, helpText = '' }) => (
  <Paper
    sx={{ p: 2, display: 'flex', alignItems: 'center', height: '100%' }}
    elevation={0}
    variant="outlined"
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Icon size={24} />
    </Box>
    <Box>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
        {helpText ? (
          <Tooltip title={helpText}>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', color: 'text.secondary' }}>
              <CircleHelp size={14} />
            </Box>
          </Tooltip>
        ) : null}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 0.25 }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

export default function HrFeriePermessi() {
  const { user } = useAuth();
  const { addJob } = useJob();
  const roleValue = String(user?.role_name ?? user?.role ?? '').trim().toLowerCase();
  const isAdmin = roleValue === 'admin';
  const departmentValue = String(user?.department_name ?? '').trim().toLowerCase();
  const isHrManager =
    isAdmin ||
    departmentValue.includes('hr') ||
    departmentValue.includes('risorse umane') ||
    departmentValue.includes('personale');
  const [ricerca, setRicerca] = useState('');
  const [filtroStato, setFiltroStato] = useState('');
  const [filtroTipoAssenzaId, setFiltroTipoAssenzaId] = useState('');
  const [sourceMode, setSourceMode] = useState('api');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saldoSaving, setSaldoSaving] = useState(false);
  const [richieste, setRichieste] = useState([]);
  const [richiesteTotal, setRichiesteTotal] = useState(0);
  const [richiestePage, setRichiestePage] = useState(0);
  const [richiesteRowsPerPage, setRichiesteRowsPerPage] = useState(10);
  const [richiesteSortBy, setRichiesteSortBy] = useState('periodo');
  const [richiesteSortDir, setRichiesteSortDir] = useState('DESC');
  const [saldoOverview, setSaldoOverview] = useState(feriePermessiMock.saldo);
  const [saldiRows, setSaldiRows] = useState([]);
  const [saldiLoading, setSaldiLoading] = useState(false);
  const [saldiSearch, setSaldiSearch] = useState('');
  const [saldiTotal, setSaldiTotal] = useState(0);
  const [saldiPage, setSaldiPage] = useState(0);
  const [saldiRowsPerPage, setSaldiRowsPerPage] = useState(10);
  const [saldiSortBy, setSaldiSortBy] = useState('anno');
  const [saldiSortDir, setSaldiSortDir] = useState('DESC');
  const [saldiFilterAnno, setSaldiFilterAnno] = useState(String(new Date().getFullYear()));
  const [saldoDialogOpen, setSaldoDialogOpen] = useState(false);
  const [adminTab, setAdminTab] = useState('richieste');
  const [dipendentiOptions, setDipendentiOptions] = useState([]);
  const [tipiAssenzaOptions, setTipiAssenzaOptions] = useState([]);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportMonth, setReportMonth] = useState(String(new Date().getMonth() + 1));
  const [reportYear, setReportYear] = useState(String(new Date().getFullYear()));
  const [reportLoading, setReportLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRichiestaId, setEditingRichiestaId] = useState(null);
  const [richiestaSelezionata, setRichiestaSelezionata] = useState(null);
  const [actionDialog, setActionDialog] = useState({
    open: false,
    azione: '',
    row: null
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [formData, setFormData] = useState({
    dipendente_id: '',
    tipo_assenza_id: '',
    data_inizio: '',
    data_fine: '',
    permesso_orario: false,
    numero_ore: '',
    ora_inizio: '',
    ora_fine: '',
    mezza_giornata: '0',
    fascia_mezza_giornata: '',
    motivazione: '',
    note_dipendente: ''
  });
  const [saldoForm, setSaldoForm] = useState({
    dipendente_id: '',
    anno: String(new Date().getFullYear()),
    tipo_assenza_id: '',
    giorni_maturati: '',
    giorni_usati: '',
    ore_maturate: '',
    ore_usate: ''
  });
  const [editingSaldoId, setEditingSaldoId] = useState(null);
  const [saldoDeleteDialog, setSaldoDeleteDialog] = useState({
    open: false,
    row: null
  });

  const permessoOrarioOption = useMemo(
    () => tipiAssenzaOptions.find((item) => item.codice === 'permesso_orario'),
    [tipiAssenzaOptions]
  );

  const mapApiRowToUi = (row) => {
    const dipendente = `${row.dipendente_cognome || ''} ${row.dipendente_nome || ''}`.trim();
    const dataInizioFormatted = formatDateToItalian(row.data_inizio);
    const dataFineFormatted = formatDateToItalian(row.data_fine);
    const oraInizioFormatted = formatTimeToHHmm(row.ora_inizio);
    const oraFineFormatted = formatTimeToHHmm(row.ora_fine);
    const intervalloOrario =
      oraInizioFormatted && oraFineFormatted
        ? `${oraInizioFormatted} - ${oraFineFormatted}`
        : '';

    const periodo =
      row.data_inizio === row.data_fine
        ? dataInizioFormatted
        : `${dataInizioFormatted} -> ${dataFineFormatted}`;
    const periodoConOrario =
      (row.tipo_assenza_codice === 'permesso_orario' && intervalloOrario)
        ? `${periodo} (${intervalloOrario})`
        : periodo;

    return {
      id: row.id,
      utente_id: row.utente_id ?? null,
      dipendente_id: row.dipendente_id,
      dipendente,
      tipo: row.tipo_assenza_codice || row.tipo_assenza_nome || '-',
      tipo_label: row.tipo_assenza_nome || row.tipo_assenza_codice || '-',
      periodo: periodoConOrario,
      intervallo_orario: intervalloOrario || null,
      ore: row.numero_ore ?? '-',
      stato: row.stato || 'bozza',
      data_inizio: row.data_inizio,
      data_fine: row.data_fine,
      mezza_giornata: Boolean(row.mezza_giornata),
      fascia_mezza_giornata: row.fascia_mezza_giornata || null,
      motivazione: row.motivazione || '',
      note_dipendente: row.note_dipendente || '',
      approvatore_nome: row.approvatore_nome || null,
      note_valutazione: row.note_valutazione || null
    };
  };

  const loadReferenceData = async () => {
    const requests = [api.get('/tipi-assenza', { params: { onlyActive: 1 } })];
    if (isAdmin) {
      requests.unshift(api.get('/dipendenti', { params: { limit: 200, sortBy: 'cognome', sortDir: 'ASC' } }));
    }

    const results = await Promise.all(requests);
    const dipRes = isAdmin ? results[0] : null;
    const tipoRes = isAdmin ? results[1] : results[0];

    if (isAdmin) {
      const dipendenti = dipRes.data?.items || [];
      setDipendentiOptions(
        dipendenti.map((d) => ({
          id: d.id,
          label: `${d.cognome || ''} ${d.nome || ''}`.trim() || `Dipendente #${d.id}`
        }))
      );
    } else {
      setDipendentiOptions([]);
    }
    setTipiAssenzaOptions(
      (tipoRes.data || []).map((t) => ({
        id: t.id,
        codice: t.codice,
        label: t.nome
      }))
    );
  };

  const loadRichieste = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filtroStato) params.stato = filtroStato;
      if (filtroTipoAssenzaId) params.tipo_assenza_id = filtroTipoAssenzaId;
      if (ricerca.trim()) params.q = ricerca.trim();
      params.paginated = 1;
      params.limit = richiesteRowsPerPage;
      params.offset = richiestePage * richiesteRowsPerPage;
      params.sortBy = richiesteSortBy;
      params.sortDir = richiesteSortDir;
      const res = await api.get('/richieste-assenza', { params });
      const items = (res.data?.items || []).map(mapApiRowToUi);
      setRichieste(items);
      setRichiesteTotal(Number(res.data?.total || 0));
      setSourceMode('api');
    } catch (error) {
      console.error('Errore caricamento richieste assenza:', error);
      setSourceMode('mock');
      const fallback = feriePermessiMock.richieste.map((row, index) => ({
        id: index + 1,
        dipendente: row.dipendente,
        tipo: row.tipo,
        tipo_label: row.tipo,
        periodo: row.periodo,
        ore: row.ore,
        stato: row.stato
      }));
      setRichieste(
        feriePermessiMock.richieste.map((row, index) => ({
          id: index + 1,
          dipendente: row.dipendente,
          tipo: row.tipo,
          tipo_label: row.tipo,
          periodo: row.periodo,
          ore: row.ore,
          stato: row.stato
        }))
      );
      setRichiesteTotal(fallback.length);
      setSaldoOverview(feriePermessiMock.saldo);
    } finally {
      setLoading(false);
    }
  };

  const loadSaldoOverview = async () => {
    try {
      const anno = new Date().getFullYear();
      const res = await api.get('/saldi-assenza/overview', { params: { anno } });
      const data = res.data || {};
      setSaldoOverview({
        ferie_residue: data.ferie_residue ?? 0,
        rol_residui: data.rol_residui ?? 0,
        permessi_retribuiti_residui: data.permessi_retribuiti_residui ?? 0
      });
    } catch (error) {
      console.error('Errore caricamento saldo assenze:', error);
      setSaldoOverview(feriePermessiMock.saldo);
    }
  };

  const loadSaldiRows = async () => {
    if (!isAdmin) return;
    try {
      setSaldiLoading(true);
      const anno = Number(saldiFilterAnno) || new Date().getFullYear();
      const res = await api.get('/saldi-assenza', {
        params: {
          paginated: 1,
          anno,
          q: saldiSearch.trim(),
          limit: saldiRowsPerPage,
          offset: saldiPage * saldiRowsPerPage,
          sortBy: saldiSortBy,
          sortDir: saldiSortDir
        }
      });
      setSaldiRows(res.data?.items || []);
      setSaldiTotal(Number(res.data?.total || 0));
    } catch (error) {
      console.error('Errore caricamento saldi assenza:', error);
      setSaldiRows([]);
      setSaldiTotal(0);
    } finally {
      setSaldiLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        await loadReferenceData();
      } catch (error) {
        console.error('Errore caricamento anagrafiche assenze:', error);
      }
      await Promise.all([
        loadRichieste(),
        loadSaldoOverview(),
        ...(isAdmin ? [loadSaldiRows()] : [])
      ]);
    };
    init();
  }, [isAdmin]);

  useEffect(() => {
    loadRichieste();
  }, [filtroStato, filtroTipoAssenzaId, richiestePage, richiesteRowsPerPage, richiesteSortBy, richiesteSortDir]);

  useEffect(() => {
    if (!isAdmin) return;
    if (adminTab !== 'saldi') return;
    loadSaldiRows();
  }, [adminTab, isAdmin, saldiFilterAnno, saldiSearch, saldiPage, saldiRowsPerPage, saldiSortBy, saldiSortDir]);

  useEffect(() => {
    if (!formData.permesso_orario) return;
    const computed = computeHoursFromTimes(formData.ora_inizio, formData.ora_fine);
    setFormData((prev) => {
      if (!prev.permesso_orario) return prev;
      if (prev.numero_ore === computed) return prev;
      return { ...prev, numero_ore: computed };
    });
  }, [formData.permesso_orario, formData.ora_inizio, formData.ora_fine]);

  const richiesteFiltrate = useMemo(() => {
    if (sourceMode === 'api') return richieste;
    const term = ricerca.trim().toLowerCase();
    return richieste.filter((row) => {
      const okStato = filtroStato ? row.stato === filtroStato : true;
      const testo = `${row.dipendente || ''} ${row.tipo || ''} ${row.periodo || ''}`.toLowerCase();
      const okRicerca = term ? testo.includes(term) : true;
      return okStato && okRicerca;
    });
  }, [richieste, filtroStato, ricerca, sourceMode]);

  const richiesteOrdinateMock = useMemo(() => {
    if (sourceMode === 'api') return richiesteFiltrate;
    const rows = [...richiesteFiltrate];
    rows.sort((a, b) => {
      let va = '';
      let vb = '';
      if (richiesteSortBy === 'dipendente') {
        va = String(a.dipendente || '').toLowerCase();
        vb = String(b.dipendente || '').toLowerCase();
      } else if (richiesteSortBy === 'tipo') {
        va = String(a.tipo || '').toLowerCase();
        vb = String(b.tipo || '').toLowerCase();
      } else if (richiesteSortBy === 'ore') {
        va = Number(a.ore || 0);
        vb = Number(b.ore || 0);
      } else if (richiesteSortBy === 'stato') {
        va = String(a.stato || '').toLowerCase();
        vb = String(b.stato || '').toLowerCase();
      } else {
        va = String(a.periodo || '').toLowerCase();
        vb = String(b.periodo || '').toLowerCase();
      }
      if (va < vb) return richiesteSortDir === 'ASC' ? -1 : 1;
      if (va > vb) return richiesteSortDir === 'ASC' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [richiesteFiltrate, richiesteSortBy, richiesteSortDir, sourceMode]);

  const richiesteRowsDisplayed = useMemo(() => {
    if (sourceMode === 'api') return richiesteFiltrate;
    const start = richiestePage * richiesteRowsPerPage;
    return richiesteOrdinateMock.slice(start, start + richiesteRowsPerPage);
  }, [richiesteFiltrate, richiesteOrdinateMock, richiestePage, richiesteRowsPerPage, sourceMode]);

  const richiesteCountDisplayed = sourceMode === 'api' ? richiesteTotal : richiesteOrdinateMock.length;

  const handleRichiesteSort = (field) => {
    setRichiestePage(0);
    if (richiesteSortBy === field) {
      setRichiesteSortDir((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
      return;
    }
    setRichiesteSortBy(field);
    setRichiesteSortDir('ASC');
  };

  const openCreateDialog = () => {
    if (sourceMode !== 'api') {
      toast.warn('Creazione disponibile solo con API attiva');
      return;
    }
    setEditingRichiestaId(null);
    setFormData({
      dipendente_id: '',
      tipo_assenza_id: '',
      data_inizio: '',
      data_fine: '',
      permesso_orario: false,
      numero_ore: '',
      ora_inizio: '',
      ora_fine: '',
      mezza_giornata: '0',
      fascia_mezza_giornata: '',
      motivazione: '',
      note_dipendente: ''
    });
    setDialogOpen(true);
  };

  const openReportDialog = () => {
    if (sourceMode !== 'api') {
      toast.warn('Report disponibile solo con API attiva');
      return;
    }
    const now = new Date();
    setReportMonth(String(now.getMonth() + 1));
    setReportYear(String(now.getFullYear()));
    setReportDialogOpen(true);
  };

  const handleStartReportPdf = async () => {
    const month = Number(reportMonth);
    const year = Number(reportYear);
    if (!Number.isFinite(month) || month < 1 || month > 12) {
      toast.error('Mese non valido');
      return;
    }
    if (!Number.isFinite(year) || year < 2000 || year > 2100) {
      toast.error('Anno non valido');
      return;
    }
    try {
      setReportLoading(true);
      const res = await api.post('/reports/hr-assenze', {
        month,
        year,
        send_email: false,
        recipient_email: '',
        include_missing_stamps: false
      });
      const jobId = res.data?.jobId;
      if (!jobId) {
        throw new Error('Job ID non ricevuto');
      }
      addJob(jobId, 'hr_absence_report');
      setReportDialogOpen(false);
      toast.success('Generazione report PDF avviata');
    } catch (error) {
      console.error('Errore avvio report assenze:', error);
      toast.error(error.response?.data?.message || error.message || 'Errore avvio report assenze');
    } finally {
      setReportLoading(false);
    }
  };

  const openEditDialog = async (row) => {
    if (sourceMode !== 'api') {
      toast.warn('Modifica disponibile solo con API attiva');
      return;
    }
    try {
      const res = await api.get(`/richieste-assenza/${row.id}`);
      const data = res.data;
      const isPermessoOrario = data.tipo_assenza_codice === 'permesso_orario';
      setEditingRichiestaId(data.id);
      setFormData({
        dipendente_id: String(data.dipendente_id || ''),
        tipo_assenza_id: String(data.tipo_assenza_id || ''),
        data_inizio: data.data_inizio ? String(data.data_inizio).split('T')[0] : '',
        data_fine: data.data_fine ? String(data.data_fine).split('T')[0] : '',
        permesso_orario: isPermessoOrario,
        numero_ore: data.numero_ore !== null ? String(data.numero_ore) : '',
        ora_inizio: data.ora_inizio ? String(data.ora_inizio).slice(0, 5) : '',
        ora_fine: data.ora_fine ? String(data.ora_fine).slice(0, 5) : '',
        mezza_giornata: data.mezza_giornata ? '1' : '0',
        fascia_mezza_giornata: data.fascia_mezza_giornata || '',
        motivazione: data.motivazione || '',
        note_dipendente: data.note_dipendente || ''
      });
      setDialogOpen(true);
    } catch (error) {
      console.error('Errore caricamento richiesta per modifica:', error);
      toast.error('Errore nel caricamento dei dati della richiesta');
    }
  };

  const handleSaveRichiesta = async () => {
    const effectiveTipoAssenzaId =
      formData.permesso_orario && !formData.tipo_assenza_id
        ? (permessoOrarioOption ? String(permessoOrarioOption.id) : '')
        : formData.tipo_assenza_id;

    if (formData.permesso_orario && !effectiveTipoAssenzaId && !permessoOrarioOption) {
      toast.error('Tipo assenza "Permesso Orario" non disponibile. Configuralo in Tipi assenza.');
      return;
    }

    if ((!isAdmin && !effectiveTipoAssenzaId) || (isAdmin && !formData.dipendente_id) || !formData.data_inizio || !formData.data_fine) {
      toast.error(
        isAdmin
          ? 'Dipendente, tipo assenza, data inizio e data fine sono obbligatori'
          : 'Tipo assenza, data inizio e data fine sono obbligatori'
      );
      return;
    }
    if (formData.permesso_orario) {
      if (!formData.ora_inizio || !formData.ora_fine) {
        toast.error('Per il permesso orario indica ora inizio e ora fine');
        return;
      }
      if (formData.data_inizio !== formData.data_fine) {
        toast.error('Per il permesso orario la data inizio deve coincidere con la data fine');
        return;
      }
      if (formData.ora_fine <= formData.ora_inizio) {
        toast.error('L\'ora fine deve essere successiva all\'ora inizio');
        return;
      }
    }

    try {
      setSaving(true);
      const payload = {
        tipo_assenza_id: Number(effectiveTipoAssenzaId),
        data_inizio: formData.data_inizio,
        data_fine: formData.data_fine,
        numero_ore: formData.numero_ore === '' ? null : Number(formData.numero_ore),
        ora_inizio: formData.permesso_orario ? formData.ora_inizio : null,
        ora_fine: formData.permesso_orario ? formData.ora_fine : null,
        mezza_giornata: formData.permesso_orario ? false : (formData.mezza_giornata === '1'),
        fascia_mezza_giornata: formData.permesso_orario ? null : (formData.mezza_giornata === '1' ? formData.fascia_mezza_giornata : null),
        motivazione: formData.motivazione || null,
        note_dipendente: formData.note_dipendente || null
      };
      if (isAdmin) {
        payload.dipendente_id = Number(formData.dipendente_id);
      }
      
      if (editingRichiestaId) {
        await api.put(`/richieste-assenza/${editingRichiestaId}`, payload);
        toast.success('Richiesta modificata con successo');
      } else {
        await api.post('/richieste-assenza', payload);
        toast.success('Richiesta creata in bozza');
      }
      
      setDialogOpen(false);
      await Promise.all([loadRichieste(), loadSaldoOverview(), ...(isAdmin ? [loadSaldiRows()] : [])]);
      
      if (richiestaSelezionata && editingRichiestaId === richiestaSelezionata.id) {
        const updated = await api.get(`/richieste-assenza/${editingRichiestaId}`);
        setRichiestaSelezionata(mapApiRowToUi(updated.data));
      }
    } catch (error) {
      console.error('Errore salvataggio richiesta:', error);
      const serverMessage =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        '';
      if (
        error.response?.status === 403 &&
        String(serverMessage).toLowerCase().includes('profilo dipendente')
      ) {
        toast.error(
          'Il tuo utente non è collegato a un profilo dipendente. Contatta un admin/HR per associare il tuo account prima di inviare richieste.'
        );
      } else {
        toast.error(serverMessage || 'Errore nella creazione della richiesta');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAzioneStato = async (row, azione) => {
    if (sourceMode !== 'api') {
      toast.warn('Azione disponibile solo con API attiva');
      return false;
    }
    try {
      await api.post(`/richieste-assenza/${row.id}/${azione}`);
      toast.success(`Richiesta ${azione} completata`);
      await Promise.all([loadRichieste(), loadSaldoOverview(), ...(isAdmin ? [loadSaldiRows()] : [])]);
      if (richiestaSelezionata?.id === row.id) {
        const updated = await api.get(`/richieste-assenza/${row.id}`);
        setRichiestaSelezionata(mapApiRowToUi(updated.data));
      }
      return true;
    } catch (error) {
      console.error(`Errore azione ${azione}:`, error);
      toast.error(error.response?.data?.message || `Errore durante l'azione ${azione}`);
      return false;
    }
  };

  const requestAzioneStato = (row, azione) => {
    if (azione === 'invia' || azione === 'annulla' || azione === 'approva' || azione === 'rifiuta') {
      setActionDialog({ open: true, azione, row });
      return;
    }
    handleAzioneStato(row, azione);
  };

  const handleSaveSaldo = async () => {
    if (!saldoForm.dipendente_id || !saldoForm.tipo_assenza_id || !saldoForm.anno) {
      toast.error('Dipendente, anno e tipo assenza sono obbligatori');
      return;
    }
    try {
      setSaldoSaving(true);
      await api.post('/saldi-assenza/upsert', {
        dipendente_id: Number(saldoForm.dipendente_id),
        anno: Number(saldoForm.anno),
        tipo_assenza_id: Number(saldoForm.tipo_assenza_id),
        giorni_maturati: saldoForm.giorni_maturati === '' ? 0 : Number(saldoForm.giorni_maturati),
        giorni_usati: saldoForm.giorni_usati === '' ? 0 : Number(saldoForm.giorni_usati),
        ore_maturate: saldoForm.ore_maturate === '' ? 0 : Number(saldoForm.ore_maturate),
        ore_usate: saldoForm.ore_usate === '' ? 0 : Number(saldoForm.ore_usate)
      });
      toast.success('Saldo aggiornato');
      setEditingSaldoId(null);
      setSaldoDialogOpen(false);
      await Promise.all([loadSaldiRows(), loadSaldoOverview()]);
    } catch (error) {
      console.error('Errore salvataggio saldo:', error);
      toast.error(error.response?.data?.message || 'Errore nel salvataggio saldo');
    } finally {
      setSaldoSaving(false);
    }
  };

  const handleEditSaldo = (row) => {
    setEditingSaldoId(row.id || null);
    setSaldoForm({
      dipendente_id: String(row.dipendente_id || ''),
      anno: String(row.anno || new Date().getFullYear()),
      tipo_assenza_id: String(row.tipo_assenza_id || ''),
      giorni_maturati: String(row.giorni_maturati ?? ''),
      giorni_usati: String(row.giorni_usati ?? ''),
      ore_maturate: String(row.ore_maturate ?? ''),
      ore_usate: String(row.ore_usate ?? '')
    });
    setSaldoDialogOpen(true);
  };

  const resetSaldoForm = () => {
    setEditingSaldoId(null);
    setSaldoForm({
      dipendente_id: '',
      anno: String(saldiFilterAnno || new Date().getFullYear()),
      tipo_assenza_id: '',
      giorni_maturati: '',
      giorni_usati: '',
      ore_maturate: '',
      ore_usate: ''
    });
  };

  const openNewSaldoDialog = () => {
    resetSaldoForm();
    setSaldoDialogOpen(true);
  };

  const askDeleteSaldo = (row) => {
    setSaldoDeleteDialog({ open: true, row });
  };

  const handleSaldiSort = (field) => {
    setSaldiPage(0);
    if (saldiSortBy === field) {
      setSaldiSortDir((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
      return;
    }
    setSaldiSortBy(field);
    setSaldiSortDir('ASC');
  };

  const giorniMaturatiNum = Number(saldoForm.giorni_maturati || 0);
  const giorniUsatiNum = Number(saldoForm.giorni_usati || 0);
  const oreMaturateNum = Number(saldoForm.ore_maturate || 0);
  const oreUsateNum = Number(saldoForm.ore_usate || 0);
  const warnGiorniOver = giorniUsatiNum > giorniMaturatiNum;
  const warnOreOver = oreUsateNum > oreMaturateNum;

  const handleDeleteSaldo = async () => {
    const row = saldoDeleteDialog.row;
    if (!row?.id) return;
    try {
      setSaldoSaving(true);
      await api.delete(`/saldi-assenza/${row.id}`);
      toast.success('Saldo eliminato');
      setSaldoDeleteDialog({ open: false, row: null });
      await Promise.all([loadSaldiRows(), loadSaldoOverview()]);
    } catch (error) {
      console.error('Errore eliminazione saldo:', error);
      toast.error(error.response?.data?.message || 'Errore nell\'eliminazione del saldo');
    } finally {
      setSaldoSaving(false);
    }
  };

  const handleConfirmAzione = async () => {
    if (!actionDialog.row || !actionDialog.azione) return;
    try {
      setActionLoading(true);
      const ok = await handleAzioneStato(actionDialog.row, actionDialog.azione);
      if (ok) {
        setActionDialog({ open: false, azione: '', row: null });
      }
    } finally {
      setActionLoading(false);
    }
  };

  const closeActionDialog = () => {
    if (actionLoading) return;
    setActionDialog({ open: false, azione: '', row: null });
  };

  const actionLabelMap = {
    invia: 'invio',
    annulla: 'annullamento',
    approva: 'approvazione',
    rifiuta: 'rifiuto'
  };
  const actionVerbMap = {
    invia: 'inviare',
    annulla: 'annullare',
    approva: 'approvare',
    rifiuta: 'rifiutare'
  };
  const actionConfirmLabelMap = {
    invia: 'Conferma invio',
    annulla: 'Conferma annullamento',
    approva: 'Conferma approvazione',
    rifiuta: 'Conferma rifiuto'
  };
  const actionLabel = actionLabelMap[actionDialog.azione] || 'operazione';
  const actionVerb = actionVerbMap[actionDialog.azione] || 'procedere con';

  const renderActionIcons = (row, { stopRowClick = false } = {}) => {
    const withOptionalStop = (fn) => (event) => {
      if (stopRowClick) event.stopPropagation();
      fn();
    };

    const isOwner = Number(row?.utente_id) === Number(user?.id);
    const canManage = isHrManager;
    const canSend = row.stato === 'bozza' && (isOwner || canManage);
    const canCancel = (row.stato === 'bozza' || row.stato === 'inviata') && (isOwner || canManage);
    const canApproveReject = row.stato === 'inviata' && canManage && !isOwner;
    const canEdit = (row.stato === 'bozza' || row.stato === 'inviata') && (isOwner || canManage) || isAdmin;

    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
        {canEdit && (
          <Tooltip title="Modifica richiesta">
            <IconButton
              size="small"
              onClick={withOptionalStop(() => openEditDialog(row))}
              sx={{ color: '#0284c7', bgcolor: 'rgba(2,132,199,0.08)', '&:hover': { bgcolor: 'rgba(2,132,199,0.16)' } }}
            >
              <EditIcon size={16} />
            </IconButton>
          </Tooltip>
        )}
        {canSend && (
          <Tooltip title="Invia richiesta">
            <IconButton
              size="small"
              onClick={withOptionalStop(() => requestAzioneStato(row, 'invia'))}
              sx={{ color: '#2563eb', bgcolor: 'rgba(37,99,235,0.08)', '&:hover': { bgcolor: 'rgba(37,99,235,0.16)' } }}
            >
              <Send size={16} />
            </IconButton>
          </Tooltip>
        )}
        {canApproveReject && (
          <>
            <Tooltip title="Approva richiesta">
              <IconButton
                size="small"
                onClick={withOptionalStop(() => requestAzioneStato(row, 'approva'))}
                sx={{ color: '#16a34a', bgcolor: 'rgba(22,163,74,0.08)', '&:hover': { bgcolor: 'rgba(22,163,74,0.16)' } }}
              >
                <CheckCircle2 size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Rifiuta richiesta">
              <IconButton
                size="small"
                onClick={withOptionalStop(() => requestAzioneStato(row, 'rifiuta'))}
                sx={{ color: '#dc2626', bgcolor: 'rgba(220,38,38,0.08)', '&:hover': { bgcolor: 'rgba(220,38,38,0.16)' } }}
              >
                <XCircle size={16} />
              </IconButton>
            </Tooltip>
          </>
        )}
        {canCancel && (
          <Tooltip title="Annulla richiesta">
            <IconButton
              size="small"
              onClick={withOptionalStop(() => requestAzioneStato(row, 'annulla'))}
              sx={{ color: '#b45309', bgcolor: 'rgba(180,83,9,0.08)', '&:hover': { bgcolor: 'rgba(180,83,9,0.16)' } }}
            >
              <CircleSlash size={16} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: '#eef2ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <FerieIcon size={20} color="#4f46e5" />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Ferie e permessi
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gestione richieste, approvazioni, saldo e calendario assenze.
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <StatWidgetHr
            title="Ferie residue"
            value={saldoOverview.ferie_residue}
            icon={FerieIcon}
            color="#2563eb"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatWidgetHr
            title="ROL residui"
            value={saldoOverview.rol_residui}
            icon={Clock3}
            color="#6366f1"
            helpText="ROL = Riduzione Orario di Lavoro (permessi maturati/ex festività)"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatWidgetHr
            title="Permessi retribuiti residui"
            value={saldoOverview.permessi_retribuiti_residui}
            icon={CheckCircle2}
            color="#22c55e"
          />
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(145, 158, 171, 0.24)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="h6">{isAdmin && adminTab === 'saldi' ? 'Gestione saldi' : 'Richieste assenza'}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isAdmin && adminTab === 'richieste' && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={openReportDialog}
                  startIcon={<FileText size={16} />}
                  disabled={sourceMode !== 'api'}
                >
                  Report PDF
                </Button>
              )}
              {(!isAdmin || adminTab === 'richieste') && (
                <Button variant="contained" size="small" onClick={openCreateDialog} disabled={sourceMode !== 'api'}>
                  + Nuova Richiesta
                </Button>
              )}
            </Box>
          </Box>
          {isAdmin && (
            <Tabs
              value={adminTab}
              onChange={(_e, value) => setAdminTab(value)}
              sx={{ mt: 1.25 }}
            >
              <Tab value="richieste" label="Richieste" />
              <Tab value="saldi" label="Gestione saldi" />
            </Tabs>
          )}
        </Box>
        {(!isAdmin || adminTab === 'richieste') && (
          <>
        <Box sx={{ p: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Cerca"
            placeholder="Dipendente, tipo, periodo..."
            value={ricerca}
            onChange={(e) => setRicerca(e.target.value)}
            sx={{ minWidth: 260 }}
          />
          {sourceMode === 'api' && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setRichiestePage(0);
                loadRichieste();
              }}
            >
              Cerca
            </Button>
          )}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="filtro-stato-label">Stato</InputLabel>
            <Select
              labelId="filtro-stato-label"
              label="Stato"
              value={filtroStato}
              onChange={(e) => {
                setFiltroStato(e.target.value);
                setRichiestePage(0);
              }}
            >
              <MenuItem value="">Tutti</MenuItem>
              <MenuItem value="bozza">bozza</MenuItem>
              <MenuItem value="inviata">inviata</MenuItem>
              <MenuItem value="approvata">approvata</MenuItem>
              <MenuItem value="rifiutata">rifiutata</MenuItem>
              <MenuItem value="annullata">annullata</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="filtro-tipo-assenza-label">Tipo assenza</InputLabel>
            <Select
              labelId="filtro-tipo-assenza-label"
              label="Tipo assenza"
              value={filtroTipoAssenzaId}
              onChange={(e) => {
                setFiltroTipoAssenzaId(e.target.value);
                setRichiestePage(0);
              }}
            >
              <MenuItem value="">Tutti</MenuItem>
              {tipiAssenzaOptions.map((t) => (
                <MenuItem key={t.id} value={String(t.id)}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sortDirection={richiesteSortBy === 'dipendente' ? richiesteSortDir.toLowerCase() : false}>
                  <TableSortLabel
                    active={richiesteSortBy === 'dipendente'}
                    direction={richiesteSortBy === 'dipendente' ? richiesteSortDir.toLowerCase() : 'asc'}
                    onClick={() => handleRichiesteSort('dipendente')}
                  >
                    Dipendente
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={richiesteSortBy === 'tipo' ? richiesteSortDir.toLowerCase() : false}>
                  <TableSortLabel
                    active={richiesteSortBy === 'tipo'}
                    direction={richiesteSortBy === 'tipo' ? richiesteSortDir.toLowerCase() : 'asc'}
                    onClick={() => handleRichiesteSort('tipo')}
                  >
                    Tipo
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={richiesteSortBy === 'periodo' ? richiesteSortDir.toLowerCase() : false}>
                  <TableSortLabel
                    active={richiesteSortBy === 'periodo'}
                    direction={richiesteSortBy === 'periodo' ? richiesteSortDir.toLowerCase() : 'asc'}
                    onClick={() => handleRichiesteSort('periodo')}
                  >
                    Periodo
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={richiesteSortBy === 'ore' ? richiesteSortDir.toLowerCase() : false}>
                  <TableSortLabel
                    active={richiesteSortBy === 'ore'}
                    direction={richiesteSortBy === 'ore' ? richiesteSortDir.toLowerCase() : 'asc'}
                    onClick={() => handleRichiesteSort('ore')}
                  >
                    Ore
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={richiesteSortBy === 'stato' ? richiesteSortDir.toLowerCase() : false}>
                  <TableSortLabel
                    active={richiesteSortBy === 'stato'}
                    direction={richiesteSortBy === 'stato' ? richiesteSortDir.toLowerCase() : 'asc'}
                    onClick={() => handleRichiesteSort('stato')}
                  >
                    Stato
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 2.5 }}>
                    <CircularProgress size={22} />
                  </TableCell>
                </TableRow>
              )}
              {richiesteRowsDisplayed.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  onClick={() => setRichiestaSelezionata(row)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{row.dipendente}</TableCell>
                  <TableCell>{row.tipo}</TableCell>
                  <TableCell>
                    {row.intervallo_orario ? (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                        <Clock3 size={14} color="#6366f1" />
                        <Typography variant="body2">{row.periodo}</Typography>
                      </Box>
                    ) : (
                      row.periodo
                    )}
                  </TableCell>
                  <TableCell>{row.ore}</TableCell>
                  <TableCell>
                    <Chip size="small" label={row.stato} color={statoColor[row.stato] || 'default'} variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    {renderActionIcons(row, { stopRowClick: true })}
                  </TableCell>
                </TableRow>
              ))}
              {!loading && richiesteRowsDisplayed.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Nessuna richiesta con i filtri correnti
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50]}
          count={richiesteCountDisplayed}
          page={richiestePage}
          onPageChange={(_event, newPage) => setRichiestePage(newPage)}
          rowsPerPage={richiesteRowsPerPage}
          onRowsPerPageChange={(event) => {
            setRichiesteRowsPerPage(parseInt(event.target.value, 10));
            setRichiestePage(0);
          }}
          labelRowsPerPage="Righe per pagina:"
        />
          </>
        )}
        {isAdmin && adminTab === 'saldi' && (
          <>
            <Box sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                label="Cerca saldi"
                placeholder="Dipendente o tipo..."
                value={saldiSearch}
                onChange={(e) => {
                  setSaldiSearch(e.target.value);
                  setSaldiPage(0);
                }}
                sx={{ width: { xs: '100%', md: 230 } }}
              />
              <TextField
                size="small"
                label="Anno"
                type="number"
                value={saldiFilterAnno}
                onChange={(e) => {
                  setSaldiFilterAnno(e.target.value);
                  setSaldiPage(0);
                }}
                sx={{ width: { xs: '100%', md: 120 } }}
              />
              <Tooltip title="Ricarica la tabella saldi con i filtri correnti">
                <span>
                  <Button variant="outlined" onClick={loadSaldiRows} disabled={saldiLoading} startIcon={<RefreshCw size={14} />}>
                    Aggiorna
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Inserisci un nuovo saldo annuale per dipendente e tipo assenza">
                <span>
                  <Button variant="contained" onClick={openNewSaldoDialog} startIcon={<Plus size={14} />}>
                    Nuovo saldo
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Per modificare un saldo esistente usa l'icona matita nella tabella">
                <Box component="span" sx={{ display: 'inline-flex', color: 'text.secondary', ml: 0.5 }}>
                  <CircleHelp size={16} />
                </Box>
              </Tooltip>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sortDirection={saldiSortBy === 'dipendente' ? saldiSortDir.toLowerCase() : false}>
                      <TableSortLabel
                        active={saldiSortBy === 'dipendente'}
                        direction={saldiSortBy === 'dipendente' ? saldiSortDir.toLowerCase() : 'asc'}
                        onClick={() => handleSaldiSort('dipendente')}
                      >
                        Dipendente
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={saldiSortBy === 'anno' ? saldiSortDir.toLowerCase() : false}>
                      <TableSortLabel
                        active={saldiSortBy === 'anno'}
                        direction={saldiSortBy === 'anno' ? saldiSortDir.toLowerCase() : 'asc'}
                        onClick={() => handleSaldiSort('anno')}
                      >
                        Anno
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={saldiSortBy === 'tipo' ? saldiSortDir.toLowerCase() : false}>
                      <TableSortLabel
                        active={saldiSortBy === 'tipo'}
                        direction={saldiSortBy === 'tipo' ? saldiSortDir.toLowerCase() : 'asc'}
                        onClick={() => handleSaldiSort('tipo')}
                      >
                        Tipo
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={saldiSortBy === 'giorni_residui' ? saldiSortDir.toLowerCase() : false}>
                      <TableSortLabel
                        active={saldiSortBy === 'giorni_residui'}
                        direction={saldiSortBy === 'giorni_residui' ? saldiSortDir.toLowerCase() : 'asc'}
                        onClick={() => handleSaldiSort('giorni_residui')}
                      >
                        Giorni residui
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={saldiSortBy === 'ore_residue' ? saldiSortDir.toLowerCase() : false}>
                      <TableSortLabel
                        active={saldiSortBy === 'ore_residue'}
                        direction={saldiSortBy === 'ore_residue' ? saldiSortDir.toLowerCase() : 'asc'}
                        onClick={() => handleSaldiSort('ore_residue')}
                      >
                        Ore residue
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {saldiLoading && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 2.5 }}>
                        <CircularProgress size={22} />
                      </TableCell>
                    </TableRow>
                  )}
                  {!saldiLoading && saldiRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{`${row.dipendente_cognome || ''} ${row.dipendente_nome || ''}`.trim()}</TableCell>
                      <TableCell>{row.anno}</TableCell>
                      <TableCell>{row.tipo_assenza_nome || row.tipo_assenza_codice}</TableCell>
                      <TableCell>{row.giorni_residui}</TableCell>
                      <TableCell>{row.ore_residue}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title="Modifica saldo">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditSaldo(row)}
                              sx={{ mr: 1 }}
                            >
                              <EditIcon size={18} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Elimina saldo">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => askDeleteSaldo(row)}
                            >
                              <DeleteIcon size={18} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!saldiLoading && saldiRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">Nessun saldo disponibile</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              rowsPerPageOptions={[10, 25, 50]}
              count={saldiTotal}
              page={saldiPage}
              onPageChange={(_event, newPage) => setSaldiPage(newPage)}
              rowsPerPage={saldiRowsPerPage}
              onRowsPerPageChange={(event) => {
                setSaldiRowsPerPage(parseInt(event.target.value, 10));
                setSaldiPage(0);
              }}
              labelRowsPerPage="Righe per pagina:"
            />
          </>
        )}
      </Card>

      <Drawer
        anchor="right"
        open={Boolean(richiestaSelezionata)}
        onClose={() => setRichiestaSelezionata(null)}
        PaperProps={{ sx: { width: { xs: '100vw', sm: 420 } } }}
      >
        <Box sx={{ p: 2.5 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Dettaglio richiesta
          </Typography>
          {richiestaSelezionata && (
            <>
              <Typography variant="body2"><strong>Dipendente:</strong> {richiestaSelezionata.dipendente}</Typography>
              <Typography variant="body2"><strong>Tipo:</strong> {richiestaSelezionata.tipo}</Typography>
              <Typography variant="body2"><strong>Periodo:</strong> {richiestaSelezionata.periodo}</Typography>
              {richiestaSelezionata.intervallo_orario && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Clock3 size={14} color="#6366f1" />
                  <Typography variant="body2">
                    <strong>Intervallo orario:</strong> {richiestaSelezionata.intervallo_orario}
                  </Typography>
                </Box>
              )}
              <Typography variant="body2"><strong>Ore:</strong> {richiestaSelezionata.ore}</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Stato:</strong> {richiestaSelezionata.stato}
              </Typography>
              {richiestaSelezionata.motivazione && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Motivazione:</strong> {richiestaSelezionata.motivazione}
                </Typography>
              )}
              {richiestaSelezionata.note_valutazione && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Note valutazione:</strong> {richiestaSelezionata.note_valutazione}
                </Typography>
              )}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Azioni richiesta</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {renderActionIcons(richiestaSelezionata)}
              </Box>
            </>
          )}
        </Box>
      </Drawer>

      <Dialog
        open={reportDialogOpen}
        onClose={() => (reportLoading ? null : setReportDialogOpen(false))}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Report assenze (PDF)</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mt: 0.5, display: 'grid', gap: 1.5 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="hr-report-month-label">Mese</InputLabel>
              <Select
                labelId="hr-report-month-label"
                label="Mese"
                value={reportMonth}
                onChange={(e) => setReportMonth(e.target.value)}
              >
                {MONTH_OPTIONS.map((m) => (
                  <MenuItem key={m.value} value={String(m.value)}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Anno"
              type="number"
              value={reportYear}
              onChange={(e) => setReportYear(e.target.value)}
            />
            <Typography variant="caption" color="text.secondary">
              Il PDF include le richieste approvate e riporta i giorni di assenza per dipendente.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)} disabled={reportLoading}>
            Chiudi
          </Button>
          <Button onClick={handleStartReportPdf} variant="contained" disabled={reportLoading}>
            {reportLoading ? 'Generazione in corso...' : 'Genera'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={actionDialog.open} onClose={closeActionDialog} fullWidth maxWidth="xs">
        <DialogTitle sx={{ pb: 1 }}>Conferma {actionLabel}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            Confermi di voler {actionVerb} questa richiesta?
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
            {actionDialog.row?.dipendente || 'Dipendente non disponibile'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tipo: {actionDialog.row?.tipo_label || actionDialog.row?.tipo || '-'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Periodo: {actionDialog.row?.periodo || '-'}
          </Typography>
          {(actionDialog.azione === 'annulla' || actionDialog.azione === 'approva' || actionDialog.azione === 'rifiuta') && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {actionDialog.azione === 'annulla' && 'L\'azione imposterà lo stato della richiesta su "annullata".'}
              {actionDialog.azione === 'approva' && 'L\'azione imposterà lo stato della richiesta su "approvata".'}
              {actionDialog.azione === 'rifiuta' && 'L\'azione imposterà lo stato della richiesta su "rifiutata".'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeActionDialog} disabled={actionLoading}>
            Chiudi
          </Button>
          <Button
            onClick={handleConfirmAzione}
            variant="contained"
            color={(actionDialog.azione === 'annulla' || actionDialog.azione === 'rifiuta') ? 'error' : 'primary'}
            disabled={actionLoading}
          >
            {actionLoading ? 'Operazione in corso...' : (actionConfirmLabelMap[actionDialog.azione] || 'Conferma')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={saldoDialogOpen}
        onClose={() => (saldoSaving ? null : setSaldoDialogOpen(false))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{editingSaldoId ? `Modifica saldo #${editingSaldoId}` : 'Nuovo saldo'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mt: 0.5, display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            <Autocomplete
              options={dipendentiOptions}
              value={dipendentiOptions.find((d) => String(d.id) === String(saldoForm.dipendente_id)) || null}
              onChange={(_event, option) => {
                setSaldoForm((prev) => ({ ...prev, dipendente_id: option ? String(option.id) : '' }));
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.label || ''}
              noOptionsText="Nessun dipendente trovato"
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Dipendente"
                  placeholder="Cerca dipendente..."
                />
              )}
            />
            <TextField
              size="small"
              label="Anno"
              type="number"
              value={saldoForm.anno}
              onChange={(e) => setSaldoForm((prev) => ({ ...prev, anno: e.target.value }))}
            />
            <FormControl size="small" fullWidth sx={{ gridColumn: { xs: '1 / -1', sm: '1 / -1' } }}>
              <InputLabel id="saldo-dialog-tipo-label">Tipo assenza</InputLabel>
              <Select
                labelId="saldo-dialog-tipo-label"
                label="Tipo assenza"
                value={saldoForm.tipo_assenza_id}
                onChange={(e) => setSaldoForm((prev) => ({ ...prev, tipo_assenza_id: e.target.value }))}
              >
                {tipiAssenzaOptions.map((t) => (
                  <MenuItem key={t.id} value={String(t.id)}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Giorni maturati"
              type="number"
              value={saldoForm.giorni_maturati}
              onChange={(e) => setSaldoForm((prev) => ({ ...prev, giorni_maturati: e.target.value }))}
            />
            <TextField
              size="small"
              label="Giorni usati"
              type="number"
              value={saldoForm.giorni_usati}
              onChange={(e) => setSaldoForm((prev) => ({ ...prev, giorni_usati: e.target.value }))}
            />
            <TextField
              size="small"
              label="Ore maturate"
              type="number"
              value={saldoForm.ore_maturate}
              onChange={(e) => setSaldoForm((prev) => ({ ...prev, ore_maturate: e.target.value }))}
            />
            <TextField
              size="small"
              label="Ore usate"
              type="number"
              value={saldoForm.ore_usate}
              onChange={(e) => setSaldoForm((prev) => ({ ...prev, ore_usate: e.target.value }))}
            />
            {(warnGiorniOver || warnOreOver) && (
              <Box sx={{ gridColumn: '1 / -1', mt: -0.25 }}>
                {warnGiorniOver && (
                  <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
                    Attenzione: giorni usati superiori ai giorni maturati.
                  </Typography>
                )}
                {warnOreOver && (
                  <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
                    Attenzione: ore usate superiori alle ore maturate.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSaldoDialogOpen(false);
              resetSaldoForm();
            }}
            disabled={saldoSaving}
          >
            Annulla
          </Button>
          <Button onClick={handleSaveSaldo} variant="contained" startIcon={<Save size={14} />} disabled={saldoSaving}>
            {saldoSaving ? 'Salvataggio...' : editingSaldoId ? 'Salva modifiche' : 'Salva saldo'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={saldoDeleteDialog.open}
        onClose={() => (saldoSaving ? null : setSaldoDeleteDialog({ open: false, row: null }))}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ pb: 1 }}>Conferma eliminazione saldo</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Confermi eliminazione saldo per{' '}
            <strong>
              {saldoDeleteDialog.row
                ? `${saldoDeleteDialog.row.dipendente_cognome || ''} ${saldoDeleteDialog.row.dipendente_nome || ''}`.trim()
                : '-'}
            </strong>
            ?
          </Typography>
          {saldoDeleteDialog.row && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {saldoDeleteDialog.row.anno} - {saldoDeleteDialog.row.tipo_assenza_nome || saldoDeleteDialog.row.tipo_assenza_codice}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaldoDeleteDialog({ open: false, row: null })} disabled={saldoSaving}>
            Annulla
          </Button>
          <Button onClick={handleDeleteSaldo} color="error" variant="contained" disabled={saldoSaving}>
            {saldoSaving ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={() => (saving ? null : setDialogOpen(false))} fullWidth maxWidth="sm">
        <DialogTitle>{editingRichiestaId ? 'Modifica richiesta assenza' : 'Nuova richiesta assenza'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
            {isAdmin && (
              <FormControl fullWidth required>
                <InputLabel id="dipendente-id-label">Dipendente</InputLabel>
                <Select
                  labelId="dipendente-id-label"
                  label="Dipendente"
                  value={formData.dipendente_id}
                  onChange={(e) => setFormData((prev) => ({ ...prev, dipendente_id: e.target.value }))}
                >
                  {dipendentiOptions.map((d) => (
                    <MenuItem key={d.id} value={String(d.id)}>{d.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl fullWidth required>
              <InputLabel id="tipo-assenza-id-label">Tipo assenza</InputLabel>
              <Select
                labelId="tipo-assenza-id-label"
                label="Tipo assenza"
                value={formData.tipo_assenza_id}
                onChange={(e) => setFormData((prev) => ({ ...prev, tipo_assenza_id: e.target.value }))}
              >
                {tipiAssenzaOptions.map((t) => (
                  <MenuItem key={t.id} value={String(t.id)}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              <TextField
                label="Data inizio"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.data_inizio}
                onChange={(e) => setFormData((prev) => ({ ...prev, data_inizio: e.target.value }))}
                required
              />
              <TextField
                label="Data fine"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.data_fine}
                onChange={(e) => setFormData((prev) => ({ ...prev, data_fine: e.target.value }))}
                required
              />
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(formData.permesso_orario)}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      permesso_orario: e.target.checked,
                      tipo_assenza_id: e.target.checked
                        ? String(permessoOrarioOption?.id || prev.tipo_assenza_id || '')
                        : prev.tipo_assenza_id,
                      mezza_giornata: e.target.checked ? '0' : prev.mezza_giornata,
                      fascia_mezza_giornata: e.target.checked ? '' : prev.fascia_mezza_giornata
                    }))
                  }
                />
              }
              label="Permesso orario"
            />

            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              <TextField
                label="Numero ore"
                type="number"
                value={formData.numero_ore}
                onChange={(e) => setFormData((prev) => ({ ...prev, numero_ore: e.target.value }))}
                inputProps={{ step: '0.25', min: 0 }}
                InputProps={{ readOnly: Boolean(formData.permesso_orario) }}
                helperText={formData.permesso_orario ? 'Calcolato automaticamente da ora inizio/fine' : 'Inserimento manuale'}
              />
              {formData.permesso_orario ? (
                <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: '1fr 1fr' }}>
                  <TextField
                    label="Ora inizio"
                    type="time"
                    InputLabelProps={{ shrink: true }}
                    value={formData.ora_inizio}
                    onChange={(e) => setFormData((prev) => ({ ...prev, ora_inizio: e.target.value }))}
                    required
                  />
                  <TextField
                    label="Ora fine"
                    type="time"
                    InputLabelProps={{ shrink: true }}
                    value={formData.ora_fine}
                    onChange={(e) => setFormData((prev) => ({ ...prev, ora_fine: e.target.value }))}
                    required
                  />
                </Box>
              ) : (
                <FormControl fullWidth>
                  <InputLabel id="mezza-giornata-label">Mezza giornata</InputLabel>
                  <Select
                    labelId="mezza-giornata-label"
                    label="Mezza giornata"
                    value={formData.mezza_giornata}
                    onChange={(e) => setFormData((prev) => ({ ...prev, mezza_giornata: e.target.value }))}
                  >
                    <MenuItem value="0">No</MenuItem>
                    <MenuItem value="1">Si</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Box>

            {!formData.permesso_orario && formData.mezza_giornata === '1' && (
              <FormControl fullWidth required>
                <InputLabel id="fascia-mezza-giornata-label">Fascia mezza giornata</InputLabel>
                <Select
                  labelId="fascia-mezza-giornata-label"
                  label="Fascia mezza giornata"
                  value={formData.fascia_mezza_giornata}
                  onChange={(e) => setFormData((prev) => ({ ...prev, fascia_mezza_giornata: e.target.value }))}
                >
                  <MenuItem value="mattina">Mattina</MenuItem>
                  <MenuItem value="pomeriggio">Pomeriggio</MenuItem>
                </Select>
              </FormControl>
            )}

            <TextField
              label="Motivazione"
              value={formData.motivazione}
              onChange={(e) => setFormData((prev) => ({ ...prev, motivazione: e.target.value }))}
              multiline
              minRows={2}
            />
            <TextField
              label="Note dipendente"
              value={formData.note_dipendente}
              onChange={(e) => setFormData((prev) => ({ ...prev, note_dipendente: e.target.value }))}
              multiline
              minRows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Annulla</Button>
          <Button onClick={handleSaveRichiesta} variant="contained" disabled={saving}>
            {saving ? 'Salvataggio...' : editingRichiestaId ? 'Salva modifiche' : 'Salva bozza'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
